import React from 'react'
import * as S from './styles'
import CardNew from 'presentation/shared/components/CardNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import DividerNew from 'presentation/shared/components/DividerNew'
import BadgeStatusNew from 'presentation/shared/components/BadgeStatusNew'
import StatusTag from 'presentation/shared/components/StatusTag'
import { ReactComponent as PendencyIcon } from 'presentation/assets/icons/pendency-icon.svg'
import { ReactComponent as ThreshIcon } from 'presentation/assets/icons/trash-icon.svg'
import theme from 'presentation/styles/theme'
import moment from 'moment'
import { ReactComponent as AlertIcon } from 'presentation/assets/icons/alert-triangle-filled.svg'
import { PasswordMaxDateValidationStatus } from 'common/enum/password-max-date-validation-status'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import { ReactComponent as CalendarIcon } from 'presentation/assets/icons/new_calendar.svg'
import { ReactComponent as RightArrowIcon } from 'presentation/assets/icons/v-arrow.svg'
import { useHistory } from 'react-router'
import { SurgicalOrdersStatus } from 'common/enum/surgical-orders-status'

type Props = {
  surgicalOrder: any
  footerColor?: keyof typeof theme.ds.colors
  onClick?: (surgical_order_id: number) => void
  onTrashClick?: (surgical_order_id: number) => void
  showTrash?: boolean
  hideFooter?: boolean
  hideStatusTag?: boolean
  flatten?: boolean
  procedures?: boolean
  schedulingDate?: boolean
  statusTag?: boolean
  trashIconList?: boolean
  id?: string
}

export default function SurgicalOrderCard({
  surgicalOrder,
  footerColor = 'terciary400',
  onClick,
  onTrashClick,
  showTrash = false,
  hideFooter = false,
  hideStatusTag = false,
  flatten = false,
  procedures,
  schedulingDate,
  statusTag,
  trashIconList,
  id
}: Props) {
  const history = useHistory()
  const getDateLabel = () => {
    switch (surgicalOrder.scheduling_status) {
      case 'scheduled':
      case 'SCHEDULED':
        return {
          label: 'Data da cirurgia',
          key: 'scheduled_date'
        }
      case 'analyze':
      case 'ANALYZE':
      case 'scheduled_in_analysis':
      case 'SCHEDULED_IN_ANALYSIS':
        return {
          label: 'Data solicitada',
          key: 'requested_date'
        }
      case 'unfinshed':
      case 'UNFINISHED':
      case 'UNSCHEDULED':
      case 'unscheduled':
      default:
        return {
          label: 'Data prevista no aviso',
          key: 'expected_date'
        }
    }
  }

  const onDelete = (event: React.MouseEvent) => {
    event.stopPropagation()
    onTrashClick && onTrashClick(surgicalOrder.surgical_order_id)
  }

  const mainProcedure = () =>
    surgicalOrder.procedures
      ?.find((procedure: any) => procedure.isMain)
      ?.description?.toLowerCase()

  const handleScheduleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    history.push('/agendamento-cirurgico/inicio', {
      surgical_order_id: surgicalOrder.surgical_order_id
    })
  }

  const showButtonsOrderStatus = (
    surgicalOrderActiveStatus: SurgicalOrdersStatus,
    surgicalOrderSchedulingStatus: string
  ) => {
    const surgicalOrderStatus = ![
      SurgicalOrdersStatus.REQUESTED_CANCELLATION,
      SurgicalOrdersStatus.CONCLUDED,
      SurgicalOrdersStatus.DENIED,
      SurgicalOrdersStatus.CANCELLED
    ].includes(surgicalOrderActiveStatus)

    const schedulingStatus =
      surgicalOrderSchedulingStatus !== 'scheduled' &&
      surgicalOrderSchedulingStatus !== 'SCHEDULED'

    return surgicalOrderStatus && schedulingStatus
  }

  return (
    <CardNew
      background={onClick || flatten ? 'neutral25' : 'white'}
      shadow={onClick && !flatten ? 'satin' : 'none'}
      border={onClick || flatten ? 'none' : 'primary300'}
      onClick={
        onClick ? () => onClick(surgicalOrder.surgical_order_id) : undefined
      }
      id={id}
    >
      {surgicalOrder.active_status !== 'ORDER_OPEN' && (
        <>
          <S.CardHeader>
            <SupportTextNew
              fontFamily="secondary"
              size="xsmall"
              color="neutral900"
            >
              Aviso {surgicalOrder.surgical_order_id || ''}
            </SupportTextNew>

            <BadgeStatusNew
              status={surgicalOrder.scheduling_status}
              fontFamily="secondary"
            />
          </S.CardHeader>

          <DividerNew marginTop="8px" marginBottom="8px" />
        </>
      )}

      <S.CardBody>
        <div>
          {!!surgicalOrder.pendencies && surgicalOrder.pendencies?.total && (
            <S.BoxPendencies>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <PendencyIcon className="pendency-icon" />
                <SupportTextNew size="xsmall" weight="bold" color="neutral900">
                  Pendências
                </SupportTextNew>
              </div>
              <S.BadgeCounter color="terciary800">
                {surgicalOrder.pendencies.total}
              </S.BadgeCounter>
            </S.BoxPendencies>
          )}
        </div>

        <div>
          <SupportTextNew weight="bold" color="neutral900" size="xxsmall">
            Paciente
          </SupportTextNew>
          <SupportTextNew capitalize size="xsmall">
            {surgicalOrder.patient?.name.toLowerCase() || '-'}
          </SupportTextNew>
        </div>

        {procedures && (
          <div>
            <SupportTextNew weight="bold" color="neutral900" size="xxsmall">
              Procedimento
            </SupportTextNew>
            <SupportTextNew capitalize size="xsmall">
              {mainProcedure() || 'Não preenchido'}
            </SupportTextNew>
          </div>
        )}

        <div>
          <SupportTextNew weight="bold" color="neutral900" size="xxsmall">
            Convênio
          </SupportTextNew>
          <SupportTextNew capitalize size="xsmall">
            {surgicalOrder.health_insurance?.name?.toLowerCase() || '-'}
          </SupportTextNew>
        </div>

        {schedulingDate && surgicalOrder?.scheduling_date && (
          <div>
            <SupportTextNew weight="bold" color="neutral900" size="xxsmall">
              {getDateLabel().label}
            </SupportTextNew>
            <SupportTextNew size="xsmall">
              {surgicalOrder.scheduling_date?.length
                ? surgicalOrder.scheduling_date.map((date: any) =>
                    date[getDateLabel().key]
                      ? moment
                          .utc(date[getDateLabel().key])
                          .format('DD [de] MMM[.] [de] YYYY')
                      : 'Não preenchido'
                  )
                : '-'}
            </SupportTextNew>
          </div>
        )}

        <div>
          <SupportTextNew weight="bold" color="neutral900" size="xxsmall">
            Unidade
          </SupportTextNew>
          <SupportTextNew capitalize size="xsmall">
            {surgicalOrder.hospital?.name
              ?.replace(/^\d+\s*-\s*/, '')
              .toLowerCase() || '-'}
          </SupportTextNew>
        </div>

        {surgicalOrder?.finalize_surgical_order?.[0]
          ?.password_max_date_validation ? (
          <div>
            <SupportTextNew weight="bold" color="neutral900" size="xxsmall">
              Senha de autorização
            </SupportTextNew>
            <SupportTextNew size="xsmall">
              <S.AlertIconWrapper
                status={
                  surgicalOrder?.finalize_surgical_order?.[0]
                    ?.password_max_date_validation_status
                }
              >
                {surgicalOrder?.finalize_surgical_order?.[0]
                  ?.password_max_date_validation_status !==
                PasswordMaxDateValidationStatus.VALID ? (
                  <AlertIcon />
                ) : (
                  ''
                )}
                Válida até{' '}
                {moment(
                  surgicalOrder?.finalize_surgical_order?.[0]
                    ?.password_max_date_validation
                )
                  .utc()
                  .format('DD/MM/YYYY')}
              </S.AlertIconWrapper>
            </SupportTextNew>
          </div>
        ) : (
          ''
        )}

        {statusTag && !hideStatusTag && surgicalOrder.active_status && (
          <StatusTag status={surgicalOrder.active_status} />
        )}

        {trashIconList && showTrash && (
          <S.TrashIconInList>
            <ThreshIcon onClick={onDelete} className="tresh-icon" />
          </S.TrashIconInList>
        )}
      </S.CardBody>

      {!hideFooter && !trashIconList && showTrash && (
        <S.CardFooter color={footerColor}>
          <ThreshIcon onClick={onDelete} className="tresh-icon" />
        </S.CardFooter>
      )}

      {showButtonsOrderStatus(
        surgicalOrder.active_status,
        surgicalOrder.scheduling_status
      ) &&
        !hideFooter &&
        !showTrash && (
          <>
            <DividerNew style={{ marginTop: 'auto' }} />
            <S.CardFooter color={footerColor}>
              <S.ButtonsWrapper>
                <ButtonNew
                  fullWidth
                  size="small"
                  icon={<CalendarIcon />}
                  iconPosition="left"
                  onClick={handleScheduleClick}
                >
                  Agendar
                </ButtonNew>
                <ButtonNew
                  fullWidth
                  size="small"
                  icon={<RightArrowIcon />}
                  iconPosition="right"
                  outlined
                  onClick={
                    onClick
                      ? () => onClick(surgicalOrder.surgical_order_id)
                      : undefined
                  }
                >
                  Ver mais
                </ButtonNew>
              </S.ButtonsWrapper>
            </S.CardFooter>
          </>
        )}
    </CardNew>
  )
}
